/**
 * Menu Action Handlers
 *
 * Centralized handlers for Electron menu actions.
 * These functions are called when menu items are clicked in the desktop app.
 */

import { condoApi, api } from './api';
import { logger } from './logger';
import { fetchPersonDevices, revokeExitDevices } from '../utils/deviceRevoke';

// Type for toast notification function
type ToastFunction = (message: string, type?: 'success' | 'error' | 'info') => void;

// Type for confirmation dialog function
type ConfirmFunction = (message: string) => Promise<boolean>;

/**
 * Force Revoke Expired Visitors
 *
 * Revokes all visitors whose access has expired based on:
 * - access_expires_at (for service providers)
 * - entry_window_expires_at (for visitors awaiting entry)
 * - valid_until (role validity end date)
 */
export const handleForceRevokeVisitors = async (
  customerId: string | null,
  showConfirmation: ConfirmFunction,
  showToast: ToastFunction
) => {
  if (!customerId) {
    showToast('Nenhum cliente selecionado', 'error');
    return;
  }

  try {
    // Fetch all visitors and service providers
    const [visitorsResponse, serviceProvidersResponse] = await Promise.all([
      condoApi.getPeople(customerId, { role_type: 'visitor' }),
      condoApi.getPeople(customerId, { role_type: 'service_provider' }),
    ]);

    const visitorsData = visitorsResponse.data?.data || visitorsResponse.data || [];
    const serviceProvidersData = serviceProvidersResponse.data?.data || serviceProvidersResponse.data || [];
    const allPeople = [...visitorsData, ...serviceProvidersData];

    // Filter expired visitors/service providers
    const now = new Date();
    const expiredPeople: Array<{ personId: string; roleId: string; name: string }> = [];

    allPeople.forEach((person: any) => {
      const role = person.roles?.find((r: any) =>
        (r.role_type === 'visitor' || r.role_type === 'service_provider') &&
        r.is_active === true
      );

      if (!role) return;

      const metadata = role.role_metadata || {};
      const accessExpiresAt = metadata.access_expires_at;
      const entryWindowExpiresAt = metadata.entry_window_expires_at;
      const validUntil = role.valid_until;

      // Check if any expiration date has passed
      const isExpired =
        (accessExpiresAt && new Date(accessExpiresAt) < now) ||
        (entryWindowExpiresAt && new Date(entryWindowExpiresAt) < now) ||
        (validUntil && new Date(validUntil) < now);

      if (isExpired) {
        expiredPeople.push({
          personId: person.id,
          roleId: role.id,
          name: `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Sem nome',
        });
      }
    });

    if (expiredPeople.length === 0) {
      showToast('Nenhum visitante expirado encontrado', 'info');
      return;
    }

    // Confirm action
    const confirmed = await showConfirmation(
      `Tem certeza que deseja revogar ${expiredPeople.length} visitante(s) expirado(s)?\n\n` +
      `Primeiros: ${expiredPeople.slice(0, 3).map(p => p.name).join(', ')}...`
    );

    if (!confirmed) return;

    // Revoke each expired visitor
    let successCount = 0;
    let failCount = 0;

    for (const person of expiredPeople) {
      try {
        // Fetch devices BEFORE revoke — zones are empty after the backend revokes access
        const devices = await fetchPersonDevices(customerId, person.personId);
        await condoApi.revokePersonRoleAuthorization(customerId, person.personId, person.roleId);
        revokeExitDevices(customerId, person.personId, person.name, devices, 3).catch(err =>
          logger.error('menuActions', 'Device removal failed', { person, error: err })
        );
        successCount++;
        logger.info('menuActions', 'Revoked expired visitor', {
          personId: person.personId,
          roleId: person.roleId,
          name: person.name,
        });
      } catch (error) {
        failCount++;
        logger.error('menuActions', 'Failed to revoke visitor', { person, error });
      }
    }

    showToast(
      `Revogação concluída: ${successCount} sucesso, ${failCount} falhas`,
      failCount === 0 ? 'success' : 'info'
    );
  } catch (error) {
    logger.error('menuActions', 'Error in handleForceRevokeVisitors', error);
    showToast('Erro ao revogar visitantes expirados', 'error');
  }
};

/**
 * Check-Out All Visitors
 *
 * Manually checks out all visitors currently inside (status='inside')
 */
export const handleCheckoutAllVisitors = async (
  customerId: string | null,
  showConfirmation: ConfirmFunction,
  showToast: ToastFunction
) => {
  if (!customerId) {
    showToast('Nenhum cliente selecionado', 'error');
    return;
  }

  try {
    // Fetch all visitors and service providers
    const [visitorsResponse, serviceProvidersResponse] = await Promise.all([
      condoApi.getPeople(customerId, { role_type: 'visitor' }),
      condoApi.getPeople(customerId, { role_type: 'service_provider' }),
    ]);

    const visitorsData = visitorsResponse.data?.data || visitorsResponse.data || [];
    const serviceProvidersData = serviceProvidersResponse.data?.data || serviceProvidersResponse.data || [];
    const allPeople = [...visitorsData, ...serviceProvidersData];

    // Filter visitors currently inside
    const insidePeople: Array<{ personId: string; roleId: string; name: string }> = [];

    allPeople.forEach((person: any) => {
      const role = person.roles?.find((r: any) =>
        (r.role_type === 'visitor' || r.role_type === 'service_provider') &&
        r.is_active === true
      );

      if (!role) return;

      const metadata = role.role_metadata || {};
      const visitorState = metadata.visitor_state;

      if (visitorState === 'inside') {
        insidePeople.push({
          personId: person.id,
          roleId: role.id,
          name: `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Sem nome',
        });
      }
    });

    if (insidePeople.length === 0) {
      showToast('Nenhum visitante presente no momento', 'info');
      return;
    }

    // Confirm action
    const confirmed = await showConfirmation(
      `Fazer check-out de ${insidePeople.length} visitante(s) presente(s)?\n\n` +
      `Primeiros: ${insidePeople.slice(0, 3).map(p => p.name).join(', ')}...`
    );

    if (!confirmed) return;

    // Check out each visitor
    let successCount = 0;
    let failCount = 0;

    for (const person of insidePeople) {
      try {
        // Check-out the visitor (backend automatically revokes access)
        await condoApi.manualCheckOut(customerId, person.personId, person.roleId);

        successCount++;
        logger.info('menuActions', 'Checked out visitor', {
          personId: person.personId,
          roleId: person.roleId,
          name: person.name,
        });
      } catch (error) {
        failCount++;
        logger.error('menuActions', 'Failed to check out visitor', { person, error });
      }
    }

    showToast(
      `Check-out concluído: ${successCount} sucesso, ${failCount} falhas`,
      failCount === 0 ? 'success' : 'info'
    );
  } catch (error) {
    logger.error('menuActions', 'Error in handleCheckoutAllVisitors', error);
    showToast('Erro ao fazer check-out de visitantes', 'error');
  }
};

/**
 * End-of-Day Cleanup
 *
 * Combines: Revoke expired + Checkout all
 */
export const handleEndOfDayCleanup = async (
  customerId: string | null,
  showConfirmation: ConfirmFunction,
  showToast: ToastFunction
) => {
  if (!customerId) {
    showToast('Nenhum cliente selecionado', 'error');
    return;
  }

  const confirmed = await showConfirmation(
    'Executar limpeza de fim de dia?\n\n' +
    'Isso irá:\n' +
    '1. Revogar todos os visitantes expirados\n' +
    '2. Fazer check-out de todos os visitantes presentes'
  );

  if (!confirmed) return;

  // Execute both operations sequentially
  await handleForceRevokeVisitors(customerId, () => Promise.resolve(true), showToast);
  await handleCheckoutAllVisitors(customerId, () => Promise.resolve(true), showToast);

  showToast('Limpeza de fim de dia concluída', 'success');
};

/**
 * Check Device Connectivity
 *
 * Shows a summary of online/offline devices
 */
export const handleCheckDeviceConnectivity = async (
  customerId: string | null,
  showToast: ToastFunction,
  showModal?: (content: React.ReactNode) => void
) => {
  if (!customerId) {
    showToast('Nenhum cliente selecionado', 'error');
    return;
  }

  try {
    const response = await api.get(`/customers/${customerId}/iot-devices`);
    const devices = response.data?.data || response.data || [];

    const onlineDevices = devices.filter((d: any) => d.is_online === true);
    const offlineDevices = devices.filter((d: any) => d.is_online !== true);

    const message =
      `Conectividade de Dispositivos:\n\n` +
      ` Online: ${onlineDevices.length}\n` +
      ` Offline: ${offlineDevices.length}\n` +
      `Total: ${devices.length}\n\n` +
      (offlineDevices.length > 0
        ? `Dispositivos offline:\n${offlineDevices.slice(0, 5).map((d: any) => `- ${d.name || d.device_id}`).join('\n')}`
        : '');

    // For now, just show a toast (TODO: implement modal)
    showToast(message, offlineDevices.length === 0 ? 'success' : 'info');

    logger.info('menuActions', 'Device connectivity check', {
      online: onlineDevices.length,
      offline: offlineDevices.length,
      total: devices.length,
    });
  } catch (error) {
    logger.error('menuActions', 'Error checking device connectivity', error);
    showToast('Erro ao verificar conectividade de dispositivos', 'error');
  }
};

/**
 * Export Today's Visitors (CSV)
 */
export const handleExportTodayVisitors = async (
  customerId: string | null,
  showToast: ToastFunction
) => {
  if (!customerId) {
    showToast('Nenhum cliente selecionado', 'error');
    return;
  }

  showToast('Funcionalidade em desenvolvimento', 'info');
  logger.info('menuActions', 'Export today visitors (not implemented)');
};

/**
 * Export Access Logs (CSV)
 */
export const handleExportAccessLogs = async (
  customerId: string | null,
  showToast: ToastFunction
) => {
  if (!customerId) {
    showToast('Nenhum cliente selecionado', 'error');
    return;
  }

  showToast('Funcionalidade em desenvolvimento', 'info');
  logger.info('menuActions', 'Export access logs (not implemented)');
};

/**
 * System Diagnostics
 */
export const handleSystemDiagnostics = async (
  showToast: ToastFunction
) => {
  showToast('Funcionalidade em desenvolvimento', 'info');
  logger.info('menuActions', 'System diagnostics (not implemented)');
};

/**
 * Clear Local Cache
 */
export const handleClearCache = async (
  showConfirmation: ConfirmFunction,
  showToast: ToastFunction
) => {
  const confirmed = await showConfirmation(
    'Limpar cache local?\n\n' +
    'Isso irá recarregar a aplicação e pode exigir novo login.'
  );

  if (!confirmed) return;

  try {
    // Clear all local storage except auth tokens
    const keycloakToken = localStorage.getItem('token');
    const keycloakRefreshToken = localStorage.getItem('refreshToken');

    localStorage.clear();
    sessionStorage.clear();

    // Restore auth tokens
    if (keycloakToken) localStorage.setItem('token', keycloakToken);
    if (keycloakRefreshToken) localStorage.setItem('refreshToken', keycloakRefreshToken);

    showToast('Cache limpo com sucesso. Recarregando...', 'success');

    // Reload after 1 second
    setTimeout(() => {
      window.location.reload();
    }, 1000);

    logger.info('menuActions', 'Cache cleared');
  } catch (error) {
    logger.error('menuActions', 'Error clearing cache', error);
    showToast('Erro ao limpar cache', 'error');
  }
};

/**
 * Daily Report (PDF)
 */
export const handleDailyReport = async (
  customerId: string | null,
  showToast: ToastFunction
) => {
  if (!customerId) {
    showToast('Nenhum cliente selecionado', 'error');
    return;
  }

  showToast('Funcionalidade em desenvolvimento', 'info');
  logger.info('menuActions', 'Daily report (not implemented)');
};

/**
 * Access Statistics
 */
export const handleAccessStatistics = async (
  customerId: string | null,
  showToast: ToastFunction
) => {
  if (!customerId) {
    showToast('Nenhum cliente selecionado', 'error');
    return;
  }

  showToast('Funcionalidade em desenvolvimento', 'info');
  logger.info('menuActions', 'Access statistics (not implemented)');
};
